import React, { useState, useEffect, useMemo } from "react";
import { Activity } from "lucide-react";
import ActivitiesSidebar from "./ActivitiesSidebar";
import ActivitiesList from "./ActivitiesList";
import FilterChips, { ChipItem } from "../../documents/FilterChips";
import DeleteJobModal from "../modals/DeleteJobModal";
import NewActivityModal, { ActivityData, NewActivityModalProps } from "./modals/NewActivityModal";
import EditJobModal from "../modals/EditJobModal";
import api, { getUserUid } from "../../../../utils/api";
import { API_ENDPOINTS } from "../../../../config";
import "../../../../styles/kanbans/activities.css";
import "../../../../styles/breadcrumbs.css";

interface ActivityFromAPI {
  uid: string;
  title: string;
  type: string;
  description?: string | null;
  is_completed: boolean;
  start_date?: string | null;
  end_date?: string | null;
  job_uid?: string | null;
  contact_uid?: string | null;
  board_uid: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string | null;
}

interface Activity {
  id: number;
  uid: string;
  title: string;
  company: string;
  position: string;
  category: string;
  status: string;
  timeAgo: string;
  note?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  is_completed?: boolean;
  job_uid?: string | null;
  board_uid?: string;
}

const filterTabs = ["All", "Due Today", "Past Due", "Pending", "Completed"];
const categories = ["Applications", "Interviews", "Offers", "Networking"];

type CategoryKey = 'all' | 'applications' | 'interviews' | 'offers' | 'networking';

interface ActivitiesProps {
  shouldOpenNewActivity?: boolean;
  onNewActivityOpened?: () => void;
  boardId?: string;
}

const formatTimeAgo = (dateString?: string | null): string => {
  if (!dateString) return "Just now";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return "1 week ago";
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
};

const getCategoryFromType = (type: string): string => {
  const normalizedType = type.trim();
  
  if (normalizedType === "Apply") {
    return "Applications";
  }
  
  if (normalizedType === "Phone Screen" || normalizedType === "Phone Interview" || normalizedType === "On Site Interview") {
    return "Interviews";
  }
  
  if (normalizedType === "Offer Received" || normalizedType === "Accept Offer" || normalizedType === "Decline Offer") {
    return "Offers";
  }
  
  if (normalizedType === "Meeting" || normalizedType === "Networking Event") {
    return "Networking";
  }
  
  return "Other";
};

export default function Activities({ shouldOpenNewActivity, onNewActivityOpened, boardId }: ActivitiesProps) {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<number | null>(null);
  const [newActivityModalOpen, setNewActivityModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editJobModalOpen, setEditJobModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);
  const [selectedBoardId, setSelectedBoardId] = useState<string | undefined>(undefined);
  const [selectedJobTitle, setSelectedJobTitle] = useState<string | undefined>(undefined);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>(undefined);
  const [kanbans, setKanbans] = useState<Kanban[]>([]);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);

        let kanbansData: Kanban[] = [];
        let allActivities: ActivityFromAPI[] = [];

        if (boardId) {
          const kanban = await api.get<{ uid: string; name: string }>(API_ENDPOINTS.kanbans.get(boardId));
          kanbansData = [{ uid: kanban.uid, name: kanban.name }];
          allActivities = await api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(boardId));
        } else {
          kanbansData = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
          const allActivitiesPromises = kanbansData.map(kanban =>
            api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(kanban.uid))
          );
          const allActivitiesArrays = await Promise.all(allActivitiesPromises);
          allActivities = allActivitiesArrays.flat();
        }

        setKanbans(kanbansData);

        const jobsMap = new Map<string, Job>();
        for (const kanban of kanbansData) {
          try {
            const jobs = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanban.uid));
            jobs.forEach(job => {
              if (!jobsMap.has(job.uid)) {
                jobsMap.set(job.uid, job);
              }
            });
          } catch {
          }
        }

        const transformedActivities: Activity[] = allActivities.map((activity) => {
          const uidStr = activity.uid.replace(/-/g, '');
          const numericId = parseInt(uidStr.substring(0, 8), 16) % 1000000;

          let company = "Unknown";
          let position = "Unknown";
          if (activity.job_uid) {
            const job = jobsMap.get(activity.job_uid);
            if (job) {
              company = job.company_name || "Unknown";
              position = job.job_title || "Unknown";
            }
          }

          return {
            id: numericId,
            uid: activity.uid,
            title: activity.title,
            company,
            position,
            category: getCategoryFromType(activity.type),
            status: activity.type,
            timeAgo: formatTimeAgo(activity.created_at || activity.updated_at),
            note: activity.description || undefined,
            startDate: activity.start_date || undefined,
            endDate: activity.end_date || undefined,
            type: activity.type,
            is_completed: activity.is_completed,
            job_uid: activity.job_uid || null,
            board_uid: activity.board_uid,
          };
        });

        setActivities(transformedActivities);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [boardId]);

  const isToday = (dateString?: string | null): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const isPastDue = (dateString?: string | null): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isTypeInCategory = (type: string | undefined, category: string): boolean => {
    if (!type) return false;
    const normalizedType = type.trim();
    switch (category) {
      case 'applications':
        return normalizedType === "Apply";
      case 'interviews':
        return normalizedType === "Phone Screen" || normalizedType === "Phone Interview" || normalizedType === "On Site Interview";
      case 'offers':
        return normalizedType === "Offer Received" || normalizedType === "Accept Offer" || normalizedType === "Decline Offer";
      case 'networking':
        return normalizedType === "Meeting" || normalizedType === "Networking Event";
      default:
        return false;
    }
  };

  const getCategoriesForFilter = (filter: string): string[] => {
    switch (filter) {
      case 'applications':
        return ['Apply'];
      case 'interviews':
        return ['Phone Screen', 'Phone Interview', 'On Site Interview'];
      case 'offers':
        return ['Offer Received', 'Accept Offer', 'Decline Offer'];
      case 'networking':
        return ['Meeting', 'Networking Event'];
      default:
        return [];
    }
  };

  const chips: ChipItem[] = useMemo(() => {
    const statusFiltered = activities.filter((activity) => {
      switch (selectedFilter) {
        case 'All':
          return true;
        case 'Due Today':
          return isToday(activity.endDate);
        case 'Past Due':
          return isPastDue(activity.endDate);
        case 'Pending':
          return !activity.is_completed;
        case 'Completed':
          return activity.is_completed === true;
        default:
          return true;
      }
    });
    
    const categoryCounts = {
      all: statusFiltered.length,
      applications: statusFiltered.filter(a => a.category === 'Applications').length,
      interviews: statusFiltered.filter(a => a.category === 'Interviews').length,
      offers: statusFiltered.filter(a => a.category === 'Offers').length,
      networking: statusFiltered.filter(a => a.category === 'Networking').length,
    };

    const result: ChipItem[] = [
      { key: 'all', label: 'All', count: categoryCounts.all },
    ];

    if (categoryCounts.applications > 0) {
      result.push({ key: 'applications', label: 'Applications', count: categoryCounts.applications });
    }
    if (categoryCounts.interviews > 0) {
      result.push({ key: 'interviews', label: 'Interviews', count: categoryCounts.interviews });
    }
    if (categoryCounts.offers > 0) {
      result.push({ key: 'offers', label: 'Offers', count: categoryCounts.offers });
    }
    if (categoryCounts.networking > 0) {
      result.push({ key: 'networking', label: 'Networking', count: categoryCounts.networking });
    }

    return result;
  }, [activities, selectedFilter]);

  const filteredActivities = useMemo(() => {
    let result = activities.filter((activity) => {
      switch (selectedFilter) {
        case 'All':
          return true;
        case 'Due Today':
          return isToday(activity.endDate);
        case 'Past Due':
          return isPastDue(activity.endDate);
        case 'Pending':
          return !activity.is_completed;
        case 'Completed':
          return activity.is_completed === true;
        default:
          return true;
      }
    });
    
    if (activeCategory !== 'all') {
      result = result.filter((activity) => 
        isTypeInCategory(activity.type, activeCategory)
      );
    }
    
    return result;
  }, [activities, selectedFilter, activeCategory]);

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key as CategoryKey);
  };

  const handleDeleteActivity = (activityId: number) => {
    setActivityToDelete(activityId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (activityToDelete === null) {
      setDeleteModalOpen(false);
      return;
    }

    const activity = activities.find(a => a.id === activityToDelete);
    if (!activity || !activity.uid) {
      setDeleteModalOpen(false);
      setActivityToDelete(null);
      return;
    }

    try {
      await api.delete(API_ENDPOINTS.activities.delete(activity.uid));
      setActivities(activities.filter(a => a.id !== activityToDelete));
    } catch (error: any) {

      
      if (error?.status === 404) {
        setActivities(activities.filter(a => a.id !== activityToDelete));
      }
    } finally {
      setDeleteModalOpen(false);
      setActivityToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setActivityToDelete(null);
  };

  const handleCreateActivity = async (data: ActivityData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!data.board) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const activityPayload = {
        title: data.title.trim(),
        type: data.category as any,
        description: data.note?.trim() || null,
        is_completed: data.completed || false,
        start_date: data.startDate ? new Date(data.startDate).toISOString() : null,
        end_date: data.endDate ? new Date(data.endDate).toISOString() : null,
        board_uid: data.board,
        job_uid: data.job || null,
        contact_uid: null,
        status: "active" as const,
      };

      await api.post(API_ENDPOINTS.activities.create, activityPayload);
      
      setNewActivityModalOpen(false);
      
      if (boardId) {
        const singleKanban = await api.get<{ uid: string; name: string }>(API_ENDPOINTS.kanbans.get(boardId));
        const kanbansData: Kanban[] = [{ uid: singleKanban.uid, name: singleKanban.name }];
        setKanbans(kanbansData);

        const allActivities = await api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(boardId));

        const jobs = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(boardId));
        const jobsMap = new Map<string, Job>();
        jobs.forEach(job => jobsMap.set(job.uid, job));

        const transformedActivities: Activity[] = allActivities.map((activity) => {
          const uidStr = activity.uid.replace(/-/g, '');
          const numericId = parseInt(uidStr.substring(0, 8), 16) % 1000000;

          let company = "Unknown";
          let position = "Unknown";
          if (activity.job_uid) {
            const job = jobsMap.get(activity.job_uid);
            if (job) {
              company = job.company_name || "Unknown";
              position = job.job_title || "Unknown";
            }
          }

          return {
            id: numericId,
            uid: activity.uid,
            title: activity.title,
            company,
            position,
            category: getCategoryFromType(activity.type),
            status: activity.type,
            timeAgo: formatTimeAgo(activity.created_at || activity.updated_at),
            note: activity.description || undefined,
            startDate: activity.start_date || undefined,
            endDate: activity.end_date || undefined,
            type: activity.type,
            is_completed: activity.is_completed,
            job_uid: activity.job_uid || null,
            board_uid: activity.board_uid,
          };
        });

        setActivities(transformedActivities);
      } else {
        const kanbansData = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
        setKanbans(kanbansData);
        const allActivitiesPromises = kanbansData.map(kanban =>
          api.get<ActivityFromAPI[]>(API_ENDPOINTS.activities.getByKanban(kanban.uid))
        );
        const allActivitiesArrays = await Promise.all(allActivitiesPromises);
        const allActivities = allActivitiesArrays.flat();

        const jobsMap = new Map<string, Job>();
        for (const kanban of kanbansData) {
          try {
            const jobs = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanban.uid));
            jobs.forEach(job => {
              if (!jobsMap.has(job.uid)) {
                jobsMap.set(job.uid, job);
              }
            });
          } catch {
          }
        }

        const transformedActivities: Activity[] = allActivities.map((activity) => {
          const uidStr = activity.uid.replace(/-/g, '');
          const numericId = parseInt(uidStr.substring(0, 8), 16) % 1000000;

          let company = "Unknown";
          let position = "Unknown";
          if (activity.job_uid) {
            const job = jobsMap.get(activity.job_uid);
            if (job) {
              company = job.company_name || "Unknown";
              position = job.job_title || "Unknown";
            }
          }

          return {
            id: numericId,
            uid: activity.uid,
            title: activity.title,
            company,
            position,
            category: getCategoryFromType(activity.type),
            status: activity.type,
            timeAgo: formatTimeAgo(activity.created_at || activity.updated_at),
            note: activity.description || undefined,
            startDate: activity.start_date || undefined,
            endDate: activity.end_date || undefined,
            type: activity.type,
            is_completed: activity.is_completed,
            job_uid: activity.job_uid || null,
            board_uid: activity.board_uid,
          };
        });

        setActivities(transformedActivities);
      }
    } catch (error: any) {

    }
  };

  const handleUpdateActivity = async (activityUid: string, updates: {
    title?: string;
    type?: string;
    description?: string | null;
    is_completed?: boolean;
    start_date?: string | null;
    end_date?: string | null;
  }) => {
    try {
      const activityPayload = {
        title: updates.title || '',
        type: updates.type || 'Other',
        description: updates.description || null,
        is_completed: updates.is_completed || false,
        start_date: updates.start_date || null,
        end_date: updates.end_date || null,
      };

      await api.post(API_ENDPOINTS.activities.update(activityUid), activityPayload);

      setActivities(prevActivities => 
        prevActivities.map(activity => {
          if (activity.uid === activityUid) {
            const updatedActivity = { ...activity };
            
            if (updates.title !== undefined) {
              updatedActivity.title = updates.title;
            }
            if (updates.type !== undefined) {
              updatedActivity.type = updates.type;
              updatedActivity.category = getCategoryFromType(updates.type);
            }
            if (updates.description !== undefined) {
              updatedActivity.note = updates.description || undefined;
            }
            if (updates.is_completed !== undefined) {
              updatedActivity.is_completed = updates.is_completed;
            }
            if (updates.type !== undefined) {
              updatedActivity.status = updates.type;
            }
            if (updates.start_date !== undefined) {
              updatedActivity.startDate = updates.start_date || undefined;
            }
            if (updates.end_date !== undefined) {
              updatedActivity.endDate = updates.end_date || undefined;
            }
            
            return updatedActivity;
          }
          return activity;
        })
      );
    } catch (error: any) {

      throw error;
    }
  };

  const handleJobClick = (jobId: string, boardId: string, jobTitle: string, companyName: string) => {
    setSelectedJobId(jobId);
    setSelectedBoardId(boardId);
    setSelectedJobTitle(jobTitle);
    setSelectedCompanyName(companyName);
    setEditJobModalOpen(true);
  };

  const handleCloseJobModal = () => {
    setEditJobModalOpen(false);
    setSelectedJobId(undefined);
    setSelectedBoardId(undefined);
    setSelectedJobTitle(undefined);
    setSelectedCompanyName(undefined);
  };

  React.useEffect(() => {
    if (shouldOpenNewActivity) {
      setNewActivityModalOpen(true);
      onNewActivityOpened?.();
    }
  }, [shouldOpenNewActivity, onNewActivityOpened]);

  return (
    <div className="activities-page">
      <ActivitiesSidebar
        filterTabs={filterTabs}
        categories={categories}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        selectedCategory={activeCategory}
        setSelectedCategory={handleCategoryChange}
      />

      <main className="activities-main">
        <FilterChips
          chips={chips}
          activeKey={activeCategory}
          onChange={handleCategoryChange}
        />

        <div style={{ borderBottom: '1px solid #e5e7eb', margin: '16px 0' }} />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
            Loading activities...
          </div>
        ) : filteredActivities.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: '#6b7280', 
            fontSize: '14px' 
          }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <Activity size={48} style={{ color: '#9ca3af' }} />
            </div>
            <div style={{ fontWeight: 600, marginBottom: '8px', color: '#374151' }}>No activities yet</div>
            {activeCategory === 'all' ? (
              <div>Click "+ Activity" to add your first activity</div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => setNewActivityModalOpen(true)}
                  className="breadcrumbs__action-btn"
                  type="button"
                >
                  {activeCategory === 'applications' && '+ application activity'}
                  {activeCategory === 'interviews' && '+ interview activity'}
                  {activeCategory === 'offers' && '+ offer activity'}
                  {activeCategory === 'networking' && '+ networking activity'}
                </button>
              </div>
            )}
          </div>
        ) : (
            <ActivitiesList
              activities={filteredActivities}
              onDeleteActivity={handleDeleteActivity}
              onUpdateActivity={handleUpdateActivity}
              onJobClick={handleJobClick}
            />
        )}
      </main>

      <DeleteJobModal
        open={deleteModalOpen}
        title="Delete Activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        confirmText="Delete Activity"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

        <NewActivityModal
          open={newActivityModalOpen}
          onClose={() => setNewActivityModalOpen(false)}
          onCommit={handleCreateActivity}
          allowedCategories={activeCategory !== 'all' ? getCategoriesForFilter(activeCategory) : undefined}
          currentBoard={kanbans.length > 0 ? kanbans[0].uid : undefined}
          availableBoards={kanbans.map(k => ({ id: k.uid, label: k.name }))}
        />

        <EditJobModal
          open={editJobModalOpen}
          onClose={handleCloseJobModal}
          jobId={selectedJobId}
          boardId={selectedBoardId}
          jobTitle={selectedJobTitle}
          companyName={selectedCompanyName}
        />
      </div>
    );
  }